/**
 * Reader view component for DEX reader workflow.
 * Orchestrates all reader subcomponents into a unified view:
 * - Payload entry form for fetching payloads
 * - Payload table for displaying fetched payloads
 * - Decrypt modal for decrypting payloads
 * - Keygen modal for generating new keypairs
 */

import { renderPayloadEntry, initPayloadEntry } from './payload-entry.js';
import { renderPayloadTable, initPayloadTable } from './payload-table.js';
import { showDecryptModal, hideDecryptModal, renderDecryptModal, initDecryptModal } from './decrypt-modal.js';
import { showKeygenModal, hideKeygenModal } from './keygen-modal.js';
import { getPayload, markPayloadComplete, parsePayloadPK } from '../../api/payloads.js';
import { showToast } from './toast.js';
import { subscribe, getState } from '../../state/store.js';
import {
  initReaderState,
  setReaderField,
  setReaderStatus,
  setReaderSort,
  addPayload,
  removePayload,
  setPrivateKey,
  clearReaderState
} from '../../state/reader.js';

// Module-level state
let cleanupFunctions = [];
let lastRefresh = 0;
const REFRESH_COOLDOWN = 5000; // 5 seconds

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Render the reader view into a container.
 * @param {HTMLElement} container - Container element
 */
export function renderReaderView(container) {
  container.innerHTML = `
    <div class="space-y-6">
      <!-- Header Section -->
      <div class="flex items-center justify-between">
        <h2 class="text-xl font-semibold text-gray-800">Reader Dashboard</h2>
        <div class="flex gap-2">
          <button
            id="refresh-btn"
            type="button"
            class="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Refresh
          </button>
          <button
            id="keygen-btn"
            type="button"
            class="px-3 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
            </svg>
            Generate Keypair
          </button>
        </div>
      </div>

      <!-- Payload Entry Section -->
      <section id="payload-entry-container"></section>

      <!-- Payload Table Section -->
      <section id="payload-table-container"></section>

      <!-- Decrypt Modal Container -->
      <div id="decrypt-modal-container"></div>

      <!-- Keygen Modal Container -->
      <div id="keygen-modal-container"></div>
    </div>
  `;
}

/**
 * Initialize the reader view with event handlers and state subscriptions.
 * @param {HTMLElement} container - Container element
 * @returns {Function} Cleanup function
 */
export function initReaderView(container) {
  // Initialize reader state
  initReaderState();

  // Reset cleanup functions array
  cleanupFunctions = [];

  // Track subcomponent cleanups
  let payloadEntryCleanup = null;
  let payloadTableCleanup = null;
  let decryptModalCleanup = null;
  let keygenModalCleanup = null;

  // Track entry form state for re-fetching
  let lastFetchParams = null;

  /**
   * Render and wire all subcomponents based on current state.
   */
  function renderSubcomponents() {
    const { reader } = getState();

    // Clean up previous subcomponents
    if (payloadEntryCleanup) {
      payloadEntryCleanup();
      payloadEntryCleanup = null;
    }
    if (payloadTableCleanup) {
      payloadTableCleanup();
      payloadTableCleanup = null;
    }

    // Render payload entry form
    const entryContainer = container.querySelector('#payload-entry-container');
    if (entryContainer) {
      renderPayloadEntry(entryContainer, {
        workflowId: '',
        sampleId: '',
        stageId: '',
        error: reader.error,
        loading: reader.status === 'loading'
      });

      payloadEntryCleanup = initPayloadEntry(entryContainer, handleFetch);
    }

    // Render payload table
    const tableContainer = container.querySelector('#payload-table-container');
    if (tableContainer) {
      renderPayloadTable(tableContainer, {
        payloads: reader.payloads || [],
        sortColumn: reader.sortColumn || 'pk',
        sortDirection: reader.sortDirection || 'desc',
        loading: reader.status === 'loading'
      });

      payloadTableCleanup = initPayloadTable(tableContainer, {
        onSort: handleSort,
        onAction: handleTableAction
      });
    }
  }

  /**
   * Handle fetch request from payload entry form.
   * @param {Object|null} identifiers - { workflowId, sampleId, stageId } or null
   * @param {string|null} error - Error message from validation
   */
  async function handleFetch(identifiers, error) {
    if (error) {
      setReaderStatus('idle', error);
      return;
    }

    if (!identifiers) {
      return;
    }

    const { workflowId, sampleId, stageId } = identifiers;

    // Store for refresh capability
    lastFetchParams = { workflowId, sampleId, stageId };

    // Set loading state
    setReaderStatus('loading');

    try {
      const payload = await getPayload(workflowId, sampleId, stageId);

      // Add payload to state (check for duplicates by pk)
      const { reader } = getState();
      const existing = (reader.payloads || []).find(p => p.pk === payload.pk);

      if (existing) {
        // Update existing payload
        const updated = (reader.payloads || []).map(p =>
          p.pk === payload.pk ? payload : p
        );
        setReaderField('payloads', updated);
        showToast('Payload updated', 'success');
      } else {
        // Add new payload
        addPayload(payload);
        showToast('Payload fetched successfully', 'success');
      }

      setReaderStatus('idle');
    } catch (err) {
      setReaderStatus('idle', err.message || 'Failed to fetch payload');
      showToast(err.message || 'Failed to fetch payload', 'error');
    }
  }

  /**
   * Handle sort column change.
   * @param {string} column - Column to sort by
   */
  function handleSort(column) {
    setReaderSort(column);
  }

  /**
   * Handle table action (decrypt or complete).
   * @param {string} action - 'decrypt' or 'complete'
   * @param {string} payloadPk - Payload partition key
   */
  function handleTableAction(action, payloadPk) {
    const { reader } = getState();
    const payload = (reader.payloads || []).find(p => p.pk === payloadPk);

    if (!payload) {
      showToast('Payload not found', 'error');
      return;
    }

    if (action === 'decrypt') {
      handleDecrypt(payload);
    } else if (action === 'complete') {
      handleComplete(payload);
    }
  }

  /**
   * Handle decrypt action - show decrypt modal.
   * @param {Object} payload - Payload to decrypt
   */
  function handleDecrypt(payload) {
    const { reader } = getState();
    const decryptContainer = container.querySelector('#decrypt-modal-container');

    if (!decryptContainer) return;

    // Clean up previous modal if exists
    if (decryptModalCleanup) {
      decryptModalCleanup();
      decryptModalCleanup = null;
    }

    // Show the decrypt modal (this sets currentPayload in the modal module)
    showDecryptModal(
      decryptContainer,
      payload,
      reader.rememberKey ? reader.privateKey : null
    );

    // Initialize with callbacks
    decryptModalCleanup = initDecryptModal(decryptContainer, {
      onClose: () => {
        hideDecryptModal(decryptContainer);
        decryptModalCleanup = null;
      },
      onDecryptComplete: ({ rememberKey }) => {
        // Prompt user to mark as complete
        const shouldComplete = window.confirm('Mark payload as complete?');
        if (shouldComplete) {
          handleComplete(payload);
        }

        // Handle remember key setting
        if (rememberKey) {
          // Get private key from textarea and store in state
          const textArea = decryptContainer.querySelector('#key-text-content');
          const keyVal = textArea?.value?.trim();
          if (keyVal) {
            setPrivateKey(keyVal, true);
          }
        }
      },
      onRememberChange: (remember) => {
        setReaderField('rememberKey', remember);
      }
    });
  }

  /**
   * Handle complete action - mark payload as complete.
   * @param {Object} payload - Payload to mark complete
   */
  async function handleComplete(payload) {
    try {
      // Parse the PK to get identifiers
      const { workflow_id, sample_id, stage_id } = parsePayloadPK(payload.pk);

      // Call API
      await markPayloadComplete(workflow_id, sample_id, stage_id);

      // Remove from local state
      removePayload(payload.pk);

      // Show success toast
      showToast('Payload marked as complete', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to mark payload complete', 'error');
    }
  }

  /**
   * Handle keypair generation button click.
   */
  function handleKeygenClick() {
    const keygenContainer = container.querySelector('#keygen-modal-container');

    if (!keygenContainer) return;

    // Clean up previous modal if exists
    if (keygenModalCleanup) {
      keygenModalCleanup();
      keygenModalCleanup = null;
    }

    // Show keygen modal
    keygenModalCleanup = showKeygenModal(keygenContainer, {
      onClose: () => {
        hideKeygenModal(keygenContainer);
        keygenModalCleanup = null;
      },
      onGenerate: () => {
        // Public key is derived from private key during decryption,
        // so no need to store it in reader state.
      }
    });
  }

  /**
   * Handle refresh button click - re-fetch all payloads.
   */
  async function handleRefreshClick() {
    const { reader } = getState();
    const payloads = reader.payloads || [];

    if (payloads.length === 0) {
      showToast('No payloads to refresh', 'info');
      return;
    }

    // Check cooldown
    const now = Date.now();
    if (now - lastRefresh < REFRESH_COOLDOWN) {
      showToast('Please wait before refreshing again', 'info');
      return;
    }
    lastRefresh = now;

    setReaderStatus('loading');

    try {
      // Re-fetch each payload by its PK
      const updatedPayloads = [];

      for (const payload of payloads) {
        try {
          const { workflow_id, sample_id, stage_id } = parsePayloadPK(payload.pk);
          const updated = await getPayload(workflow_id, sample_id, stage_id);
          updatedPayloads.push(updated);
        } catch (err) {
          // Payload may have been deleted or completed - skip
          console.warn(`Failed to refresh payload ${payload.pk}:`, err.message);
        }
      }

      setReaderField('payloads', updatedPayloads);
      setReaderStatus('idle');
      showToast(`Refreshed ${updatedPayloads.length} payload(s)`, 'success');
    } catch (err) {
      setReaderStatus('idle', err.message || 'Refresh failed');
      showToast('Refresh failed', 'error');
    }
  }

  /**
   * Handle visibility change for auto-refresh on tab focus.
   */
  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      const { reader } = getState();
      const payloads = reader.payloads || [];

      // Only auto-refresh if we have payloads and cooldown has passed
      if (payloads.length > 0) {
        const now = Date.now();
        if (now - lastRefresh >= REFRESH_COOLDOWN) {
          handleRefreshClick();
        }
      }
    }
  }

  // Subscribe to reader state changes
  const unsubscribe = subscribe((state, path) => {
    if (path.includes('reader') || path === '*') {
      renderSubcomponents();
    }
  });
  cleanupFunctions.push(unsubscribe);

  // Initial render
  renderReaderView(container);
  renderSubcomponents();

  // Wire header buttons
  const keygenBtn = container.querySelector('#keygen-btn');
  const refreshBtn = container.querySelector('#refresh-btn');

  if (keygenBtn) {
    keygenBtn.addEventListener('click', handleKeygenClick);
    cleanupFunctions.push(() => keygenBtn.removeEventListener('click', handleKeygenClick));
  }

  if (refreshBtn) {
    refreshBtn.addEventListener('click', handleRefreshClick);
    cleanupFunctions.push(() => refreshBtn.removeEventListener('click', handleRefreshClick));
  }

  // Wire visibility change for auto-refresh
  document.addEventListener('visibilitychange', handleVisibilityChange);
  cleanupFunctions.push(() => document.removeEventListener('visibilitychange', handleVisibilityChange));

  // Return cleanup function
  return function cleanup() {
    // Clean up modals
    if (decryptModalCleanup) {
      decryptModalCleanup();
    }
    if (keygenModalCleanup) {
      keygenModalCleanup();
    }
    if (payloadEntryCleanup) {
      payloadEntryCleanup();
    }
    if (payloadTableCleanup) {
      payloadTableCleanup();
    }

    // Call all cleanup functions
    cleanupFunctions.forEach((fn) => fn());
    cleanupFunctions = [];

    // Clear reader state
    clearReaderState();
  };
}
